import { useCallback, useEffect, useRef, useState } from 'react';

import { searchApi } from '../api';

/**
 * One query's lifecycle: run it, keep the results, and keep three failure
 * conditions apart that all look like "nothing" on screen.
 *
 * The distinction the API is careful about has to survive into the UI or it was
 * pointless. `/search/*` returns 503 rather than an empty list when the store is
 * unavailable, because "nothing is similar" and "similarity search is not running"
 * call for completely different responses from whoever is looking. So this hook
 * reports four states rather than a boolean:
 *
 * * `unavailable` — the store cannot answer. Say so; do not say "no results".
 * * `empty index` — it answered, but there is nothing indexed yet. The caller
 *   reads that off `status`, not off the result, because an empty result against
 *   an empty index is a different problem from one against a full index.
 * * no results — a real, actionable answer.
 * * results.
 *
 * Queries are aborted on re-query. Without that, a slow first search can land
 * after a fast second one and overwrite it, which reads as the search returning
 * the wrong answer.
 */
export function useSearch(run) {
  const [results, setResults] = useState(null);
  const [meta, setMeta] = useState(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  const controller = useRef(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      controller.current?.abort();
    };
  }, []);

  const search = useCallback(
    async (payload) => {
      controller.current?.abort();
      const current = new AbortController();
      controller.current = current;

      setSearching(true);
      setError(null);
      try {
        const data = await run(payload, { signal: current.signal });
        if (!mounted.current || current.signal.aborted) return null;
        setResults(data.results || []);
        setMeta({
          count: data.count ?? (data.results || []).length,
          elapsedMs: data.elapsed_ms,
          metric: data.metric,
          collection: data.collection,
          queryKind: data.query_kind,
        });
        return data;
      } catch (cause) {
        // An abort is this hook superseding its own request, not a failure worth
        // showing: the newer query owns the screen.
        if (cause?.name === 'AbortError' || current.signal.aborted) return null;
        if (!mounted.current) return null;
        setError(cause);
        setResults(null);
        setMeta(null);
        return null;
      } finally {
        if (mounted.current && controller.current === current) setSearching(false);
      }
    },
    [run],
  );

  const reset = useCallback(() => {
    controller.current?.abort();
    setResults(null);
    setMeta(null);
    setError(null);
    setSearching(false);
  }, []);

  return {
    results,
    meta,
    searching,
    error,
    /** True only for the store being unavailable — never for an empty result. */
    unavailable: error?.code === 'vector_search_unavailable',
    /** A query has been run, whatever it returned. Distinguishes "no matches" from "not asked yet". */
    ran: results !== null,
    search,
    reset,
  };
}

/**
 * Whether search can answer at all, and how much is indexed.
 *
 * Fetched once on mount rather than per query: it describes the deployment, not
 * the request. `rows` is what lets the page tell an unindexed store from an
 * unhelpful query — the difference between "run the backfill" and "try different
 * words", which is not a distinction the user can be expected to guess.
 */
export function useSearchStatus() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setStatus(await searchApi.status());
      setError(null);
    } catch (cause) {
      // This endpoint is meant to answer even when Milvus is down, so a failure
      // here means the API itself is unreachable — a different problem, reported
      // as itself.
      setError(cause);
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const collections = status?.collections || {};
  const available = Boolean(status?.enabled && status?.reachable && status?.embedder?.available);

  return {
    status,
    loading,
    error,
    available,
    refresh,
    /** Rows in one collection, or 0 when it does not exist yet. */
    rows: (name) => collections[name]?.rows ?? 0,
    /**
     * Why search cannot answer, in the words of the thing that is wrong. Ordered
     * so the first cause is reported rather than the last symptom — an unreachable
     * Milvus also makes the collections look absent.
     */
    reason: () => {
      if (!status) return 'The API is not reachable.';
      if (!status.enabled) {
        return 'Similarity search is switched off on this deployment (MILVUS_ENABLED is false).';
      }
      if (!status.reachable) {
        return status.error || `Milvus is not reachable at ${status.uri}.`;
      }
      if (!status.embedder?.available) {
        return 'The worker is not serving embedding models, so a query cannot be vectorised.';
      }
      return null;
    },
  };
}
